interface AdditionalInfoAnswer {
  questionId: number;
  answer: string;
}

const QUESTION_ID_REGEX = /:id_(\d+)$/;
const ANSWER_TYPE_REGEX = /:([^:]+):id_\d+$/;

const SEPARATOR = "\0";

const formatGroupedList = (value: unknown): string => {
  const items = Array.isArray(value) ? value : [value];
  const grouped: Record<string, string[]> = {};

  (items as string[]).forEach((item) => {
    const sepIndex = item.indexOf(SEPARATOR);
    if (sepIndex > -1) {
      const category = item.substring(0, sepIndex);
      const val = item.substring(sepIndex + 1);
      if (!grouped[category]) grouped[category] = [];
      grouped[category].push(val);
    } else {
      if (!grouped[item]) grouped[item] = [];
    }
  });

  return JSON.stringify(
    Object.entries(grouped).map(([category, vals]) => ({
      category,
      value: vals,
    })),
  );
};

const formatDateField = (value: unknown): string => {
  const dateValue = value as {
    isValid?: () => boolean;
    toISOString?: () => string;
  };
  return dateValue?.isValid?.()
    ? dateValue.toISOString!()
    : String(value);
};

const formatDropdown = (value: unknown): string => {
  return Array.isArray(value) ? JSON.stringify(value) : String(value);
};

const formatFile = (value: unknown): string => {
  if (Array.isArray(value)) {
    const urls = value
      .map((f: any) => f.url || f.response?.url)
      .filter(Boolean);
    return JSON.stringify(urls);
  }
  return String(value);
};

const formatAnswer = (answerType: string, value: unknown): string => {
  switch (answerType) {
    case "grouped_list":
      return formatGroupedList(value);
    case "date_field":
      return formatDateField(value);
    case "dropdown":
      return formatDropdown(value);
    case "file":
      return formatFile(value);
    default:
      return String(value);
  }
};

export const extractAdditionalInfo = (
  formValues: Record<string, any>,
): AdditionalInfoAnswer[] | undefined => {
  const additionalInfoRaw = formValues.additionalInfo;
  if (!additionalInfoRaw) return undefined;

  const result: AdditionalInfoAnswer[] = [];

  Object.values(additionalInfoRaw).forEach((section: any) => {
    if (!section || typeof section !== "object") return;

    Object.entries(section).forEach(([key, value]) => {
      if (value === undefined || value === null || value === "") return;

      const idMatch = key.match(QUESTION_ID_REGEX);
      if (!idMatch) return;

      const typeMatch = key.match(ANSWER_TYPE_REGEX);
      const answerType = typeMatch ? typeMatch[1] : "open_field";

      result.push({
        questionId: Number(idMatch[1]),
        answer: formatAnswer(answerType, value),
      });
    });
  });

  return result.length > 0 ? result : undefined;
};
